/**
 * The palette's Ask and Act modes (DesignSystem 5.1). Typing "?" asks a question over live
 * state; ">" lists actions only; anything else mixes the best of both into the usual
 * navigation. Mod+S turns an answer into a saved view in one keystroke.
 */
import { useCallback, useMemo } from 'react';
import type { KeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { hasMod, undoable } from '@orgus/ui';
import { closePalette } from '../../lib/commands.ts';
import { useOrgus } from '../../lib/store.ts';
import { buildActions, matchActs, stopSessionsAction } from '../act/catalogue.ts';
import { usePageSelection } from '../act/selection.ts';
import type { ActAction } from '../act/catalogue.ts';
import { evaluateAsk, looksLikeAsk } from '../ask/evaluate.ts';
import type { AskResult } from '../ask/evaluate.ts';
import { parseAsk } from '../ask/parse.ts';
import type { AskEntity } from '../ask/parse.ts';
import { askSnapshot, useNames } from '../views/useWhere.ts';
import { deleteMyView, normalizeSearch, saveMyView } from '../views/store.ts';
import { isCommandTitle, matchScore, valueTitle } from './match.ts';

export type PaletteMode = 'ask' | 'act' | 'mixed';

const PAGE_ENTITY: Record<string, AskEntity> = {
  '/sessions': 'sessions',
  '/machines': 'machines',
  '/audit': 'audit',
  '/projects': 'checkouts',
  '/pipelines': 'pipelines',
  '/spend': 'spend',
};

export interface Power {
  mode: PaletteMode;
  /** The words after any "?" or ">" prefix. */
  text: string;
  answer: AskResult | null;
  /** Score of the answer for cmdk ordering (0 hides it). */
  answerScore: number;
  acts: ActAction[];
  filter: (value: string, search: string, keywords?: string[]) => number;
  onKeyDown: (e: KeyboardEvent) => void;
  /** Open an answer as a list view (keeps the page's other filters when it is the same page). */
  openAnswer: (a: AskResult, extra?: Record<string, string>) => void;
  saveAnswer: (a: AskResult) => void;
}

export function modeOf(query: string): { mode: PaletteMode; text: string } {
  const q = query.trimStart();
  if (q.startsWith('?')) return { mode: 'ask', text: q.slice(1).trim() };
  if (q.startsWith('>')) return { mode: 'act', text: q.slice(1).trim() };
  return { mode: 'mixed', text: q.trim() };
}

function viewName(a: AskResult): string {
  const t = a.query.raw.replace(/^[?>\s]+/, '').trim();
  return (t.charAt(0).toUpperCase() + t.slice(1)).slice(0, 60) || 'Saved view';
}

/**
 * `commandTitles`: the palette's command titles. Typing one exactly (any case, any spacing)
 * names that command, so no answer in words is offered ahead of it: Enter runs "Add machine"
 * rather than filtering machines by "add". Start with ? to ask anyway.
 */
export function usePower(query: string, open: boolean, commandTitles: readonly string[] = []): Power {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const names = useNames();
  const { mode, text } = modeOf(query);
  const pageEntity = PAGE_ENTITY[pathname];

  // Live data, read when the palette opens and as the query changes (not on every patch).
  const sessions = useOrgus((s) => (open ? s.sessions : null));
  const machines = useOrgus((s) => (open ? s.machines : null));
  const pipelines = useOrgus((s) => (open ? s.pipelines : null));
  const checkouts = useOrgus((s) => (open ? s.checkouts : null));
  const jobs = useOrgus((s) => (open ? s.jobs : null));

  // Ask mode lists no actions, so it never builds them.
  const needActs = open && mode !== 'ask';
  const actions = useMemo(() => {
    if (!needActs || !sessions || !machines || !pipelines || !checkouts || !jobs) return [];
    const st = useOrgus.getState();
    return buildActions({ sessions, machines, projects: st.projects, checkouts, pipelines, jobs, now: Date.now() });
  }, [needActs, sessions, machines, pipelines, checkouts, jobs]);

  const selection = usePageSelection();
  const focus = useMemo(() => {
    const p = new URLSearchParams(search);
    const fromUrl = ['session', 'machine', 'pipeline', 'project', 'entry'].map((k) => p.get(k)).filter((v): v is string => !!v);
    return [...fromUrl, ...(selection.ids.length ? ['selection'] : [])];
  }, [search, selection.ids.length]);

  // Rows selected on the page become one action on all of them, ranked first.
  const selectionActs = useMemo(() => {
    if (!needActs || selection.kind !== 'sessions' || !selection.ids.length) return [];
    const st = useOrgus.getState();
    const list = selection.ids.map((id) => st.sessions[id]).filter((s): s is NonNullable<typeof s> => !!s && s.status !== 'ended');
    if (!list.length) return [];
    const a = stopSessionsAction(list, st, 'selection.stop', `Stop the ${list.length === 1 ? 'selected session' : `${list.length} selected sessions`}`);
    a.refs.push('selection');
    a.keywords.push('selected', 'selection');
    return [a];
  }, [needActs, selection]);

  const acts = useMemo(() => {
    if (!open) return [];
    if (mode === 'ask') return [];
    const words = text;
    if (mode === 'mixed' && !words) return matchActs('', [...selectionActs, ...actions], { path: pathname, focus }, 3).map((x) => x.action);
    const found = matchActs(words, [...selectionActs, ...actions], { path: pathname, focus }, mode === 'act' ? 12 : 4);
    // In the mixed list, only actions every word matched and that lead with their verb.
    return found.filter((x) => mode === 'act' || x.score >= words.split(/\s+/).length + 0.5).map((x) => x.action);
  }, [open, mode, text, actions, selectionActs, pathname, focus]);

  const answer = useMemo<AskResult | null>(() => {
    if (!open || mode === 'act' || !text) return null;
    if (mode === 'mixed' && isCommandTitle(text, commandTitles)) return null;
    const q = parseAsk(text, { ...names, defaultEntity: mode === 'ask' ? pageEntity : undefined });
    if (mode === 'mixed' && !(q.entityNamed && looksLikeAsk(q) && (q.clauses.length > 0 || q.count || q.groupBy))) return null;
    return evaluateAsk(q, askSnapshot());
    // sessions/machines stand in for "the data changed" while open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, text, names, pageEntity, sessions, machines, commandTitles]);

  const answerScore = answer ? (mode === 'ask' ? 1 : 0.97) : 0;

  const filter = useCallback(
    (value: string, s: string, keywords?: string[]) => {
      if (value.startsWith('ask:')) return answerScore ? answerScore - Number(value.split(':')[1] ?? 0) * 0.0001 : 0;
      if (value.startsWith('act:')) {
        const i = Number(value.split(':')[1] ?? 0);
        return mode === 'act' ? 0.99 - i * 0.001 : 0.9 - i * 0.001;
      }
      if (mode === 'ask') return 0;
      const clean = modeOf(s).text;
      if (!clean) return 1;
      return matchScore(clean, valueTitle(value), keywords);
    },
    [answerScore, mode],
  );

  const openAnswer = useCallback(
    (a: AskResult, extra: Record<string, string> = {}) => {
      const target = { ...a.view.params, ...extra };
      const p = a.view.path === pathname ? new URLSearchParams(search) : new URLSearchParams();
      // Same page: the answer's clauses replace the filter in words; other filters stay.
      for (const k of ['where', 'view', 'session', 'entry', ...Object.keys(target)]) p.delete(k);
      for (const [k, v] of Object.entries(target)) p.set(k, v);
      closePalette();
      navigate(`${a.view.path}${p.toString() ? `?${p.toString()}` : ''}`);
    },
    [navigate, pathname, search],
  );

  const saveAnswer = useCallback(
    (a: AskResult) => {
      const name = viewName(a);
      const qs = new URLSearchParams(a.view.params).toString();
      const v = saveMyView({ name, path: a.view.path, search: normalizeSearch(a.view.path, qs) });
      closePalette();
      const p = new URLSearchParams(v.search);
      p.set('view', name);
      navigate(`${v.path}?${p.toString()}`);
      undoable({ title: `Saved view "${name}"`, description: 'Open it again from Views or by typing its name here.', undo: () => void deleteMyView(v.id) });
    },
    [navigate],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (answer && hasMod(e) && !e.altKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        e.stopPropagation();
        saveAnswer(answer);
      }
    },
    [answer, saveAnswer],
  );

  return { mode, text, answer, answerScore, acts, filter, onKeyDown, openAnswer, saveAnswer };
}
